import {
    useRef,
    useState
} from 'react';

import { SimpleGrid, Text } from '@mantine/core';

import { useOrders } from '../hooks/useOrders.ts';
import { useOrdersSelection } from '../hooks/useOrdersSelection.ts';
import { FormDrawer } from '../drawers/FormDrawer.tsx';
import { update } from '../services/OrdersService.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import { DataTableColumn } from "mantine-datatable";
import { DataTableTokenPagination } from "../../../components/DataTableTokenPagination.tsx";
import { Order } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

export const RecordsTable = () => {
    const {selectedRecords, setSelectedRecords} = useOrdersSelection();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Order | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useOrders();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleRecordClick = (record: Order) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: Order) => {
        if (selectedRecord?.id) {
            const { createdByUsername: _createdByUsername, createdBy: _createdBy, created: _created, updated: _updated, ...rest } = values;
            await update(selectedRecord.id, rest as Order);
            queryClient.invalidateQueries({ queryKey: ['orders'] });
        }
    };

    const columns: DataTableColumn<Order>[] = [
        {
            accessor: 'id',
            title: 'Order ID',
            render: (record: Order) => (
                <Text
                    c="blue"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRecordClick(record)}
                >
                    {record.id}
                </Text>
            )
        },
        {
            accessor: 'subscriptionId',
            title: 'Subscription ID',
            render: (record: Order) => (
                <Link to={`/customers/${record.customerId}/subscriptions/${record.subscriptionId}`} style={{ textDecoration: 'none' }}>
                    <Text c="blue" style={{ cursor: 'pointer' }}>
                        {record.subscriptionId}
                    </Text>
                </Link>
            )
        },
        {
            accessor: 'created',
            title: 'Created',
            render: (record: Order) => (
                <Text size="sm">
                    {record.created ? new Date(record.created).toLocaleDateString() : '-'}
                </Text>
            )
        },
    ];

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword} />

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken} handleLoadMore={fetchRecords} />

            <FormDrawer
                record={selectedRecord || {} as Order}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
